import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { MoreVertical, Pencil, Zap, Trash2, Radar, LineChart, Crosshair } from "lucide-react";
import { useBotPhotos } from "../../hooks/useBotPhotos";
import Loading from "../../components/UI/Loading/Loading";
import BotAvatar from "../../components/BotAvatar/BotAvatar";
import StatusPill from "../../components/StatusPill/StatusPill";

const MAX_AVATARS = 4;

/* ---- Menu de ações (portal, fecha em scroll/clique fora) ---- */
function RowMenu({ onEdit, onTest, onDelete }) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);
    const MENU_W = 176;

    const place = () => {
        const b = btnRef.current?.getBoundingClientRect();
        if (!b) return;
        setCoords({ top: b.bottom + 4, left: b.right - MENU_W });
    };
    const toggle = () => { if (!open) place(); setOpen((v) => !v); };

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const close = () => setOpen(false);
        document.addEventListener("mousedown", handler);
        window.addEventListener("scroll", close, true);
        window.addEventListener("resize", close);
        return () => {
            document.removeEventListener("mousedown", handler);
            window.removeEventListener("scroll", close, true);
            window.removeEventListener("resize", close);
        };
    }, [open]);

    const item = (icon, label, fn, danger) => (
        <button
            onClick={() => { setOpen(false); fn(); }}
            className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition hover:bg-white/[0.06] ${danger ? "text-[#f87171]" : "text-white/80"}`}
        >
            {icon}{label}
        </button>
    );

    return (
        <>
            <button ref={btnRef} className="action-btn" onClick={toggle} aria-label="Mais opções">
                <MoreVertical size={16} />
            </button>
            {open && coords && createPortal(
                <div
                    ref={menuRef}
                    style={{ position: "fixed", top: coords.top, left: coords.left, width: MENU_W }}
                    className="rounded-xl border border-white/[0.1] bg-[#161b24] shadow-[0_16px_40px_rgba(0,0,0,0.5)] py-1 z-[999]"
                >
                    {item(<Pencil size={14} />, "Editar", onEdit)}
                    {item(<Zap size={14} />, "Testar pixel", onTest)}
                    <div className="my-1 h-px bg-white/[0.08]" />
                    {item(<Trash2 size={14} />, "Excluir", onDelete, true)}
                </div>,
                document.body
            )}
        </>
    );
}

/* ---- Tile colorido pela plataforma do perfil ---- */
function PlatformTile({ profile }) {
    const p = profile.platform;
    if (p === "tiktok" || (!profile.facebook?.pixel_id && profile.tiktok?.pixel_id)) {
        return <div className="tk-tile tk-tile--tiktok"><i className="fab fa-tiktok" /></div>;
    }
    if (p === "facebook" || profile.facebook?.pixel_id) {
        return <div className="tk-tile tk-tile--facebook"><i className="fab fa-facebook" /></div>;
    }
    return <div className="tk-tile tk-tile--default"><Radar size={18} /></div>;
}

/* ---- Chips compactos de serviço ---- */
function ServiceChips({ profile }) {
    const chips = [];
    if (profile.facebook?.pixel_id) chips.push(["facebook", <i className="fab fa-facebook" />, "Pixel"]);
    if (profile.tiktok?.pixel_id) chips.push(["tiktok", <i className="fab fa-tiktok" />, "TikTok"]);
    if (profile.utmify?.api_token) chips.push(["utmify", <LineChart size={12} strokeWidth={2.5} />, "UTMify"]);
    if (profile.tracky_api_token) chips.push(["tracky", <Crosshair size={12} strokeWidth={2.5} />, "Tracky"]);

    if (chips.length === 0) {
        return <span className="tk-chip tk-chip--empty">Sem serviço</span>;
    }
    return chips.map(([key, icon, label]) => (
        <span key={key} className={`tk-chip tk-chip--${key}`}>
            {icon} {label}
        </span>
    ));
}

/* ---- Pilha de avatares dos bots (capada) ---- */
function BotStack({ bots, photoUrls, size = 30 }) {
    const total = bots?.length || 0;
    if (total === 0) {
        return <span className="tk-stack__label tk-stack__label--empty">Nenhum bot</span>;
    }
    const shown = bots.slice(0, MAX_AVATARS);
    const rest = total - shown.length;
    const restNames = bots.slice(MAX_AVATARS).map((b) => b.bot_username || b.bot_name).join(", ");

    return (
        <div className="tk-stack">
            <div className="tk-stack__avatars">
                {shown.map((bot) => (
                    <BotAvatar
                        key={bot.id}
                        photoUrl={photoUrls[bot.id]}
                        size={size}
                        title={bot.bot_username || bot.bot_name}
                    />
                ))}
                {rest > 0 && (
                    <span className="tk-stack__more" style={{ width: size, height: size }} title={restNames}>
                        +{rest}
                    </span>
                )}
            </div>
            <span className="tk-stack__label">{total} bot{total === 1 ? "" : "s"}</span>
        </div>
    );
}

export default function TrackingList({ profiles, loading, onEdit, onDelete, onTestPixel, bots = [] }) {
    const photoUrls = useBotPhotos(bots);

    const PAGE_SIZE = 10;
    const [page, setPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(profiles.length / PAGE_SIZE));
    useEffect(() => { setPage((p) => Math.min(p, totalPages)); }, [totalPages]);
    const pageItems = profiles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const statusPill = (p) => (
        <StatusPill color={p.is_active ? "green" : "red"} pulse={p.is_active}>
            {p.is_active ? "Ativo" : "Inativo"}
        </StatusPill>
    );

    if (loading) return <Loading text="Carregando perfis..." />;

    if (profiles.length === 0) {
        return (
            <div className="empty-state" style={{ textAlign: "center", padding: "2.5em 1em" }}>
                <Radar size={34} className="mx-auto mb-4 text-white/30" />
                <h4>Nenhum perfil configurado</h4>
                <p style={{ color: "#666" }}>
                    Crie seu primeiro perfil de tracking para começar a rastrear conversões.
                </p>
            </div>
        );
    }

    return (
        <div className="mt-6">
            <div className="tk-list">
                {pageItems.map((profile) => (
                    <div key={profile.id} className={`tk-card ${profile.is_active ? "" : "tk-card--inactive"}`}>
                        {/* ===== Desktop ===== */}
                        <div className="tk-row">
                            <PlatformTile profile={profile} />

                            <div className="tk-id">
                                <div className="tk-id__text">
                                    <div className="tk-id__head">
                                        <span className="tk-id__name" title={profile.name}>{profile.name}</span>
                                        {statusPill(profile)}
                                    </div>
                                    <div className="tk-badges">
                                        <ServiceChips profile={profile} />
                                    </div>
                                </div>
                            </div>

                            <BotStack bots={profile.bots} photoUrls={photoUrls} />

                            <div className="tk-actions">
                                <button
                                    className="btn-alert"
                                    onClick={() => onTestPixel(profile)}
                                    title="Enviar evento de teste para o pixel"
                                >
                                    <Zap size={14} /> Testar
                                </button>
                                <RowMenu
                                    onEdit={() => onEdit(profile)}
                                    onTest={() => onTestPixel(profile)}
                                    onDelete={() => onDelete(profile.id)}
                                />
                            </div>
                        </div>

                        {/* ===== Mobile ===== */}
                        <div className="tk-mobile-card">
                            <div className="tk-mhead">
                                <div className="flex items-center gap-3 min-w-0">
                                    <PlatformTile profile={profile} />
                                    <div className="min-w-0">
                                        <div className="tk-id__name" title={profile.name}>{profile.name}</div>
                                        <div className="mt-1">{statusPill(profile)}</div>
                                    </div>
                                </div>
                                <RowMenu
                                    onEdit={() => onEdit(profile)}
                                    onTest={() => onTestPixel(profile)}
                                    onDelete={() => onDelete(profile.id)}
                                />
                            </div>

                            <div className="tk-badges" style={{ marginTop: 12 }}>
                                <ServiceChips profile={profile} />
                            </div>

                            <div className="tk-mfoot">
                                <BotStack bots={profile.bots} photoUrls={photoUrls} size={28} />
                                <button
                                    className="btn-alert"
                                    onClick={() => onTestPixel(profile)}
                                    title="Enviar evento de teste para o pixel"
                                >
                                    <Zap size={14} /> Testar
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {totalPages > 1 && (
                <div className="tk-pager">
                    <div className="tk-pager__info">
                        Página {page} de {totalPages} · {profiles.length} perfis
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="btn-secondary"
                            style={{ opacity: page === 1 ? 0.5 : 1 }}
                        >
                            <i className="fas fa-chevron-left" /> Anterior
                        </button>
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="btn-primary"
                            style={{ opacity: page === totalPages ? 0.5 : 1 }}
                        >
                            Próximo <i className="fas fa-chevron-right" />
                        </button>
                    </div>
                </div>
            )}

            <div className="push-tip mt-6">
                <i className="fas fa-exclamation-triangle" />
                <div>
                    <strong>Atenção:</strong> Use o Redirecionador nativo da Spectrum para rastrear corretamente as vendas das suas campanhas.
                </div>
            </div>
        </div>
    );
}
